/**
 * 关键帧生成「配置 + 二次确认」弹窗
 *
 * 与角色 / 物品 / 场景的"生成新候选"面板对齐：用户可选择
 *  - 模型（默认项目级 imageModelId / 第一个可用模型）
 *  - 尺寸（1024x1024 / 1792x1024）
 *  - 画质（low 标准 / medium 高清 / high 超清，默认 low）
 *
 * 选择改变时实时调 previewKeyframe 重新预估积分 + 显示模型 / 模式 / 引用图数。
 *
 * 真正的扣费在用户点击「确认生成」后由 ShotInspector 调 generateKeyframe 触发，
 * 同时把当前选择的 modelId / size / quality 一起传过去。
 */

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, Plus, X, Coins, Sparkles, Cpu, AlertCircle } from 'lucide-react';
import toast from '../../../../components/Toast';
import {
  directorApi,
  DirectorApiError,
} from '../../services/directorApi';
import { api as imageApi, type ModelConfig } from '../../../../services/api';
import {
  DIRECTOR_IMAGE_SIZE_OPTIONS,
  getDefaultDirectorImageSize,
  type DirectorAspectRatio,
  type DirectorImageSize,
} from '../../utils/imageSize';

const SECTION_LABEL =
  'flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500';
const FIELD =
  'w-full rounded-2xl border border-slate-200 bg-white px-3.5 py-2 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-sky-300 focus:ring-2 focus:ring-sky-100';

const QUALITY_LABEL: Record<string, string> = {
  low: '标准',
  medium: '高清',
  high: '超清',
};

const MODE_LABEL: Record<'text2img' | 'img2img', string> = {
  text2img: '文生图',
  img2img: '参考图（含角色 / 物品 / 场景）',
};

interface Props {
  open: boolean;
  shotId: string;
  projectAspectRatio: DirectorAspectRatio;
  injectedCharacterIds: string[];
  injectedPropIds: string[];
  injectedSceneId: string | null;
  /** 引用图数量（角色 + 物品 + 场景），仅作信息展示 */
  refCount: number;
  currentCredits: number;
  /** 提交（真正调 generateKeyframe）：父组件实现 */
  submitting?: boolean;
  onCancel: () => void;
  onConfirm: (input: { modelId: string; size: string; quality: 'low' | 'medium' | 'high' }) => void;
}

export function KeyframeGenerateConfirmDialog({
  open,
  shotId,
  projectAspectRatio,
  injectedCharacterIds,
  injectedPropIds,
  injectedSceneId,
  refCount,
  currentCredits,
  submitting,
  onCancel,
  onConfirm,
}: Props) {
  const [models, setModels] = useState<ModelConfig[]>([]);
  const [modelId, setModelId] = useState('');
  const [size, setSize] = useState<DirectorImageSize>(() =>
    getDefaultDirectorImageSize(projectAspectRatio)
  );
  const [quality, setQuality] = useState<'low' | 'medium' | 'high'>('low');

  const [estimatedCredits, setEstimatedCredits] = useState<number | null>(null);
  const [previewMode, setPreviewMode] = useState<'text2img' | 'img2img'>('text2img');
  const [previewModelName, setPreviewModelName] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState(false);
  /** 解析后的真实参考图数量 */
  const [refCountResolved, setRefCountResolved] = useState<number>(0);
  /** 当前模型 img2img 渠道允许的参考图上限 */
  const [refCountMax, setRefCountMax] = useState<number>(0);
  /** 真实参考图超过模型上限 */
  const [exceedsRefLimit, setExceedsRefLimit] = useState<boolean>(false);
  /** 当前模型是否配 img2img 渠道（参考图模式下为 false → 阻止生成） */
  const [hasImg2ImgChannel, setHasImg2ImgChannel] = useState<boolean>(true);

  // 打开时加载模型列表 + 触发首轮预估
  useEffect(() => {
    if (!open) return;
    setEstimatedCredits(null);
    setPreviewModelName('');
    setSize(getDefaultDirectorImageSize(projectAspectRatio));
    imageApi
      .getModels()
      .then((d) => {
        setModels(d.data);
        if (!modelId && d.data.length > 0) {
          setModelId(d.data[0].id);
        }
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // 选择变化时调 preview 重新预估
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setPreviewLoading(true);
    directorApi
      .previewKeyframe(shotId, {
        injectedCharacterIds: injectedCharacterIds.length > 0 ? injectedCharacterIds : undefined,
        injectedPropIds: injectedPropIds.length > 0 ? injectedPropIds : undefined,
        injectedSceneId: injectedSceneId ?? undefined,
        modelId: modelId || undefined,
        size,
        quality,
      })
      .then((preview) => {
        if (cancelled) return;
        setEstimatedCredits(preview.estimatedCredits);
        setPreviewMode(preview.mode);
        setPreviewModelName(preview.modelName);
        setRefCountResolved(preview.referenceImageCount);
        setRefCountMax(preview.maxReferenceImageCount);
        setExceedsRefLimit(preview.exceedsReferenceLimit);
        setHasImg2ImgChannel(preview.hasImg2ImgChannel);
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(err instanceof DirectorApiError ? err.message : '预估积分失败');
        setEstimatedCredits(null);
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, modelId, size, quality]);

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  const insufficient = estimatedCredits !== null && currentCredits < estimatedCredits;
  // 参考图模式下若模型未配 img2img 渠道，提交一定会失败 → 提前阻止
  const missingImg2ImgChannel = previewMode === 'img2img' && !hasImg2ImgChannel;
  const canSubmit =
    !!modelId &&
    estimatedCredits !== null &&
    !insufficient &&
    !exceedsRefLimit &&
    !missingImg2ImgChannel &&
    !submitting;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <button
        type="button"
        aria-label="关闭"
        className="absolute inset-0 cursor-default"
        onClick={onCancel}
      />
      <div
        className="relative flex max-h-[88vh] w-full max-w-md flex-col overflow-hidden rounded-[28px] bg-white shadow-2xl shadow-slate-950/30"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 顶部 hero */}
        <div className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-5 text-white">
          <div className="absolute right-[-40px] top-[-40px] h-32 w-32 rounded-full bg-sky-400/20 blur-2xl" />
          <div className="relative flex items-start justify-between gap-3">
            <div>
              <div className="mb-1.5 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] font-medium text-slate-200">
                <Sparkles className="h-3 w-3 text-sky-200" />
                生成关键帧候选
              </div>
              <h2 className="text-lg font-semibold leading-tight">把当前提示词生成为一张新候选</h2>
            </div>
            <button
              type="button"
              onClick={onCancel}
              className="rounded-full bg-white/10 p-1.5 text-white transition hover:bg-white/20"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto bg-slate-50 p-5">
          {/* 模型 */}
          <div className="space-y-1.5">
            <label className={SECTION_LABEL}>
              <Cpu className="h-3 w-3" /> 图片模型
            </label>
            <select
              value={modelId}
              onChange={(e) => setModelId(e.target.value)}
              className={FIELD}
            >
              <option value="">默认（项目设置）</option>
              {models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>

          {/* 尺寸 + 画质 */}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <label className={SECTION_LABEL}>尺寸</label>
              <div className="grid grid-cols-3 gap-1">
                {DIRECTOR_IMAGE_SIZE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setSize(opt.value as typeof size)}
                    className={`rounded-xl border px-2 py-1.5 text-xs font-medium leading-none transition ${
                      size === opt.value
                        ? 'border-slate-950 bg-slate-950 text-white shadow-sm'
                        : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <label className={SECTION_LABEL}>画质</label>
              <div className="grid grid-cols-3 gap-1.5">
                {[
                  { value: 'low', label: '标准' },
                  { value: 'medium', label: '高清' },
                  { value: 'high', label: '超清' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setQuality(opt.value as typeof quality)}
                    className={`rounded-2xl border px-2 py-2 text-sm font-medium transition ${
                      quality === opt.value
                        ? 'border-slate-950 bg-slate-950 text-white shadow-md'
                        : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* 模式 + 引用图（信息展示） */}
          <div className="rounded-2xl bg-white p-3 ring-1 ring-slate-100">
            <div className="flex items-center justify-between gap-3 text-[11px]">
              <span className="uppercase tracking-[0.18em] text-slate-400">模式</span>
              <span className="truncate text-sm font-semibold text-slate-900">
                {MODE_LABEL[previewMode]}
              </span>
            </div>
            {previewModelName && (
              <div className="mt-2 flex items-center justify-between gap-3 text-[11px]">
                <span className="uppercase tracking-[0.18em] text-slate-400">实际模型</span>
                <span className="truncate text-sm font-semibold text-slate-900">
                  {previewModelName}
                </span>
              </div>
            )}
            {refCount > 0 && (
              <div className="mt-2 flex items-center justify-between gap-3 text-[11px]">
                <span className="uppercase tracking-[0.18em] text-slate-400">参考图</span>
                <span
                  className={`text-sm font-semibold ${
                    exceedsRefLimit ? 'text-rose-600' : 'text-sky-700'
                  }`}
                >
                  {previewMode === 'img2img' && refCountMax > 0
                    ? `${refCountResolved} / ${refCountMax} 张`
                    : `${refCount} 张`}
                </span>
              </div>
            )}
          </div>

          {exceedsRefLimit && (
            <div className="flex items-start gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-xs text-rose-700">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span className="flex-1 leading-5">
                注入的参考图共 {refCountResolved} 张，超过当前图片模型 img2img 上限（
                {refCountMax} 张）。请减少 @ 角色 / # 物品 / 场景的数量，或在项目设置切换支持更多参考图的图片模型。
              </span>
            </div>
          )}

          {missingImg2ImgChannel && (
            <div className="flex items-start gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-xs text-rose-700">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span className="flex-1 leading-5">
                当前图片模型没有配置「参考图（img2img）」渠道，无法处理注入的角色 / 物品 / 场景。请改用其他模型，或先清空 @ / # 注入再生成。
              </span>
            </div>
          )}

          {/* 积分 */}
          <div className="rounded-2xl bg-white p-3 ring-1 ring-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-slate-400">
                <Coins className="h-3 w-3" /> 预计消耗
              </div>
              <div className="text-base font-semibold text-sky-700">
                {previewLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                ) : estimatedCredits !== null ? (
                  `${estimatedCredits} 积分`
                ) : (
                  '—'
                )}
              </div>
            </div>
            <div className="mt-2 flex items-center justify-between text-[11px]">
              <span className="text-slate-400">当前余额</span>
              <span className={insufficient ? 'text-rose-500' : 'text-slate-600'}>
                {currentCredits} 积分
              </span>
            </div>
          </div>

          {insufficient && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-xs text-rose-700">
              积分不足，请先充值或兑换
            </div>
          )}

          <p className="text-[11px] leading-5 text-slate-500">
            候选会作为本镜的关键帧出现，可锁定为本镜首帧。
            生成失败会自动退还积分；生成成功后即扣费，与最终是否锁定无关。
          </p>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 bg-white px-5 py-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            取消
          </button>
          <button
            type="button"
            onClick={() => onConfirm({ modelId, size, quality })}
            disabled={!canSubmit}
            className="inline-flex items-center gap-2 rounded-2xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {estimatedCredits !== null
              ? `确认生成（${estimatedCredits} 积分 / ${QUALITY_LABEL[quality]}）`
              : '确认生成'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
